import { Document } from '@contentful/rich-text-types';
import { Box } from '@opendoor/bricks-next';
import { extractNavData } from 'cms/navHeaderUtils';
import GlobalStyles from 'components/shared/GlobalStyles';
import { ISiteFooter, ISiteHeader } from 'declarations/contentful';
import useQueryParams from 'helpers/queryParams';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import InspectionBanner from './InspectionBanner';
import GlassHeader from './shared/header/glass/GlassHeader';
import Header from './shared/header/Header';
import { useInspectionBannerExperiment } from './useInspectionBannerExperiment';
import GetCashOfferModal from './VisitorModal';

const MaintenanceBanner = dynamic(() => import('../shared/MaintenanceBanner'));
const Footer = dynamic(() => import('./shared/footer/Footer'));

interface LayoutProps {
  header?: ISiteHeader;
  slug: string;
  children: React.ReactNode;
  footer?: ISiteFooter;
  extraLegalText?: Document;
  showEligibilityLegal?: boolean;
  showCustomerTestimonialLegal?: boolean;
  showThePdpCopyrightLegal?: boolean;
  showPromotionLegal?: boolean;
  setModalElement?: boolean;
  showModal?: boolean;
  modalTitle?: string;
  modalDescription?: string;
  modalAnalyticsId?: string;
  hideHeader?: boolean;
  hideFooter?: boolean;
  headerVariant?: string | null;
}

const Layout = ({
  header,
  slug,
  footer,
  extraLegalText,
  showEligibilityLegal,
  showCustomerTestimonialLegal,
  showThePdpCopyrightLegal,
  showPromotionLegal,
  children,
  showModal,
  modalTitle,
  modalDescription,
  modalAnalyticsId,
  hideHeader,
  hideFooter,
  headerVariant,
}: LayoutProps) => {
  const resolvedHeaderVariant = headerVariant ?? null;
  const useGlassHeader = resolvedHeaderVariant === 'treatment';

  const { variant: inspectionBannerVariant } = useInspectionBannerExperiment();
  // Only show inspection banner on homepage variants for treatment group
  // Matches: '/', '/homepage-kt', '/homepage-v2', '/landing-pages-v2/homepage-kt', etc.
  const isHomepage = slug === '/' || slug.toLowerCase().includes('homepage');
  const showInspectionBanner = isHomepage && inspectionBannerVariant === 'treatment';

  const navData = extractNavData(header);
  const { mainNavLinks, menuPanelNavLinks, megaNavData, ctaTitle, ctaLink } = navData;

  const { getOfferExperiment } = useQueryParams();
  const showGetCashOfferModal = showModal && getOfferExperiment === 'treatment';

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://images.opendoor.com" />
        <link rel="dns-prefetch" href="https://images.opendoor.com" />
        {/* A Cloudflare Worker rule redirects demo?.(simplersell.opendoor).com/fonts to the actual font object, so
          do not blindly copy and paste these entries to another application— they might not magically work!
         */}
        <link
          rel="preload"
          crossOrigin="anonymous"
          href="/fonts/graphik-regular/Graphik-Regular.woff2"
          as="font"
        />
        <link
          rel="preload"
          crossOrigin="anonymous"
          href="/fonts/graphik-medium/Graphik-Medium.woff2"
          as="font"
        />
        <link
          rel="preload"
          crossOrigin="anonymous"
          href="/fonts/graphik-semibold/Graphik-Semibold.woff2"
          as="font"
        />
        <link
          rel="preload"
          crossOrigin="anonymous"
          href="/fonts/graphik-bold/Graphik-Bold.woff2"
          as="font"
        />
      </Head>
      <GlobalStyles />
      <MaintenanceBanner />
      {showInspectionBanner && <InspectionBanner />}
      {showGetCashOfferModal && (
        <GetCashOfferModal
          {...{
            title: modalTitle!,
            description: modalDescription!,
            analyticsId: modalAnalyticsId!,
          }}
        />
      )}
      <Box position="relative" display="block">
        {!hideHeader &&
          (useGlassHeader ? (
            <GlassHeader
              menuPanelNavLinks={menuPanelNavLinks}
              mainNavLinks={mainNavLinks}
              ctaTitle={ctaTitle}
              ctaLink={ctaLink}
              slug={slug}
              megaNavData={megaNavData}
            />
          ) : (
            <Header
              menuPanelNavLinks={menuPanelNavLinks}
              mainNavLinks={mainNavLinks}
              ctaTitle={ctaTitle}
              ctaLink={ctaLink}
              slug={slug}
              megaNavData={megaNavData}
            />
          ))}
        {/* eslint-disable @opendoor/no-html-elements */}
        <main tabIndex={-1}>
          <Box width="100%" m="$0" display="block">
            {children}
            {!hideFooter && footer && (
              <Footer
                siteFooter={footer}
                extraLegalText={extraLegalText}
                showCustomerTestimonialLegal={showCustomerTestimonialLegal}
                showEligibilityLegal={showEligibilityLegal}
                showPromotionLegal={showPromotionLegal}
                showThePdpCopyrightLegal={showThePdpCopyrightLegal}
              />
            )}
          </Box>
        </main>
      </Box>
    </>
  );
};

export default Layout;
